/**
 * TaskCategoryBadge - the visual grouping pill on the Tasks list
 * (Greg, 2026-08-08: tell client-facing work from internal work at a
 * glance). Same pill shape as StatusBadge. Colors are Greg's
 * 2026-08-09 picks:
 *   - Reminder: amber (matches the internal-note / follow-up family)
 *   - Client: red
 *   - Development: blue
 *   - DevOps: orange
 *   - Business: green
 * A null category renders nothing - uncategorized rows stay quiet.
 */
import type { TaskCategory } from "@/types/api";

const CATEGORY_INFO: Record<TaskCategory, { label: string; classes: string }> =
  {
    reminder: { label: "Reminder", classes: "bg-amber-100 text-amber-800" },
    client: { label: "Client", classes: "bg-red-100 text-red-800" },
    development: {
      label: "Development",
      classes: "bg-blue-100 text-blue-800",
    },
    devops: { label: "DevOps", classes: "bg-orange-100 text-orange-800" },
    business: { label: "Business", classes: "bg-green-100 text-green-800" },
  };

export const TASK_CATEGORY_OPTIONS: {
  value: TaskCategory;
  label: string;
}[] = [
  { value: "client", label: "Client" },
  { value: "development", label: "Development" },
  { value: "devops", label: "DevOps" },
  { value: "business", label: "Business" },
];

export function TaskCategoryBadge({
  category,
}: {
  category: TaskCategory | null;
}): JSX.Element | null {
  if (!category) return null;
  const info = CATEGORY_INFO[category] ?? {
    label: category,
    classes: "bg-neutral-200 text-neutral-600",
  };
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${info.classes}`}
      data-testid="task-category-badge"
      data-category={category}
    >
      {info.label}
    </span>
  );
}

export default TaskCategoryBadge;
